import lightGallery from 'lightgallery';
import 'lightgallery/css/lg-autoplay.css';
import 'lightgallery/css/lg-fullscreen.css';
import 'lightgallery/css/lg-rotate.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lightgallery.css';
import lgAutoplay from 'lightgallery/plugins/autoplay';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgRotate from 'lightgallery/plugins/rotate';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { AttachedFilesStore } from '~modules/attachedFiles/AttachedFilesStore';

interface IGalleryProps {
  attachedFilesStore: AttachedFilesStore;
}

export interface IGalleryRef {
  openGallery: (clickedFileId?: number | null) => void;
}

type EventMethod = 'on' | 'addEventListener';

interface LightGalleryInstance {
  on?: (event: string, handler: () => void) => void;
  off?: (event: string, handler: () => void) => void;
  destroy: () => void;
  openGallery: (index: number) => void;
}

// Константы
const GALLERY_PLUGINS = [lgThumbnail, lgZoom, lgRotate, lgFullscreen, lgAutoplay];

const GALLERY_CONFIG = {
  plugins: GALLERY_PLUGINS,
  speed: 500,
  licenseKey: 'gpl-v3',
  dynamic: true,
  autoplay: true,
  slideShowAutoplay: false,
  slideShowInterval: 5000,
  progressBar: false,
};

const CLOSE_EVENT_NAME = 'lgBeforeClose';
const LG_CONTAINER_SELECTOR = '.lg-container';

export const Gallery = forwardRef<IGalleryRef, IGalleryProps>((props, ref) => {
  const { attachedFilesStore } = props;

  const galleryRef = useRef<HTMLDivElement>(null);
  const lightGalleryInstance = useRef<LightGalleryInstance | null>(null);
  const closeHandlerRef = useRef<(() => void) | null>(null);
  const eventMethodRef = useRef<EventMethod | null>(null);

  // Вспомогательные функции

  /**
   * Отправляет сообщение о состоянии галереи
   */
  const sendGalleryMessage = useCallback((galleryOpen: boolean) => {
    window.postMessage(
      {
        type: 'ATTACHED_DOCUMENTS',
        galleryOpen,
      },
      '*',
    );
  }, []);

  /**
   * Удаляет обработчик события закрытия галереи
   */
  const removeCloseHandler = useCallback(() => {
    if (!closeHandlerRef.current) return;

    const handler = closeHandlerRef.current;

    if (eventMethodRef.current === 'on' && lightGalleryInstance.current?.off) {
      lightGalleryInstance.current.off(CLOSE_EVENT_NAME, handler);
    } else if (eventMethodRef.current === 'addEventListener') {
      const eventHandler = handler as EventListener;
      galleryRef.current?.removeEventListener(CLOSE_EVENT_NAME, eventHandler);
      document.removeEventListener(CLOSE_EVENT_NAME, eventHandler);
      window.removeEventListener(CLOSE_EVENT_NAME, eventHandler);
    }

    closeHandlerRef.current = null;
    eventMethodRef.current = null;
  }, []);

  /**
   * Устанавливает обработчик события закрытия галереи
   */
  const setupCloseHandler = useCallback(() => {
    const handleClose = () => {
      sendGalleryMessage(false);
    };

    // Пробуем использовать метод on на экземпляре галереи (если доступен)
    if (lightGalleryInstance.current?.on) {
      lightGalleryInstance.current.on(CLOSE_EVENT_NAME, handleClose);
      closeHandlerRef.current = handleClose;
      eventMethodRef.current = 'on';
      return;
    }

    // Используем addEventListener на различных элементах
    const eventHandler = (e: Event) => {
      if (e.type === CLOSE_EVENT_NAME) {
        handleClose();
      }
    };

    galleryRef.current?.addEventListener(CLOSE_EVENT_NAME, eventHandler);
    document.addEventListener(CLOSE_EVENT_NAME, eventHandler);
    window.addEventListener(CLOSE_EVENT_NAME, eventHandler);

    closeHandlerRef.current = eventHandler as any;
    eventMethodRef.current = 'addEventListener';
  }, [sendGalleryMessage]);

  /**
   * Добавляет обработчик на контейнер lightGallery после его создания
   */
  const attachHandlerToContainer = useCallback(() => {
    // Используем requestAnimationFrame для поиска контейнера после его создания
    requestAnimationFrame(() => {
      const lgContainer = document.querySelector(LG_CONTAINER_SELECTOR);

      if (lgContainer && closeHandlerRef.current && eventMethodRef.current === 'addEventListener') {
        const handler = closeHandlerRef.current as EventListener;
        lgContainer.addEventListener(CLOSE_EVENT_NAME, handler);
      }
    });
  }, []);

  /**
   * Создает массив изображений для галереи из загруженных файлов
   */
  const createGalleryImages = useCallback((files: typeof attachedFilesStore.uploadedFiles) => {
    const apiUrl = import.meta.env.VITE_APP_API_URL;

    return files.map((file) => ({
      src: `${apiUrl}${file.documentUrl || file.previewUrl || ''}`,
      thumb: `${apiUrl}${file.previewUrl || file.documentUrl || ''}`,
    }));
  }, []);

  /**
   * Вычисляет стартовый индекс для открытия галереи
   */
  const calculateStartIndex = useCallback(
    (clickedFileId: number | null | undefined, filteredFiles: typeof attachedFilesStore.uploadedFiles) => {
      if (clickedFileId === undefined || clickedFileId === null) {
        return 0;
      }

      const filteredIndex = filteredFiles.findIndex((file) => file.fileId === clickedFileId);

      return filteredIndex !== -1 ? filteredIndex : 0;
    },
    [],
  );

  /**
   * Уничтожает текущий экземпляр галереи
   */
  const destroyGallery = useCallback(() => {
    if (lightGalleryInstance.current) {
      lightGalleryInstance.current.destroy();
      lightGalleryInstance.current = null;
    }
  }, []);

  // Effects

  useEffect(() => {
    return () => {
      removeCloseHandler();
      destroyGallery();
    };
  }, [removeCloseHandler, destroyGallery]);

  // Handlers

  const handleOpenGallery = useCallback(
    (clickedFileId?: number | null) => {
      // Проверяем наличие файлов
      if (!attachedFilesStore.uploadedFiles.length) {
        return;
      }

      // Фильтруем файлы с превью или документом
      const filteredFiles = attachedFilesStore.uploadedFiles.filter((file) => file.previewUrl || file.documentUrl);

      if (filteredFiles.length === 0) {
        return;
      }

      if (!galleryRef.current) {
        return;
      }

      // Очищаем предыдущий экземпляр и обработчики
      removeCloseHandler();
      destroyGallery();

      // Создаем данные для галереи
      const images = createGalleryImages(filteredFiles);
      const startIndex = calculateStartIndex(clickedFileId, filteredFiles);

      // Инициализируем галерею
      lightGalleryInstance.current = lightGallery(galleryRef.current, {
        ...GALLERY_CONFIG,
        dynamicEl: images,
      }) as LightGalleryInstance;

      // Устанавливаем обработчик закрытия
      setupCloseHandler();

      // Открываем галерею
      setTimeout(() => {
        if (lightGalleryInstance.current) {
          lightGalleryInstance.current.openGallery(startIndex);
          attachHandlerToContainer();
          sendGalleryMessage(true);
        }
      });
    },
    [
      attachedFilesStore.uploadedFiles,
      removeCloseHandler,
      destroyGallery,
      createGalleryImages,
      calculateStartIndex,
      setupCloseHandler,
      attachHandlerToContainer,
      sendGalleryMessage,
    ],
  );

  useImperativeHandle(ref, () => ({
    openGallery: handleOpenGallery,
  }));

  //Renders

  return <div ref={galleryRef} style={{ display: 'none' }} />;
});
